/**
 * Cleans and normalizes a URL string
 * Handles malformed URLs with quotes, double protocols, missing colons, etc.
 * @param {string} url - The URL to clean
 * @returns {string} - The cleaned URL
 */
export const cleanUrl = (url) => {
  if (!url) {
    return null;
  }

  // Clean the URL: remove quotes, stray whitespace, and fix common issues.
  let cleanedUrl = String(url)
    .trim()
    .replace(/^['"]+|['"]+$/g, '') // Remove surrounding quotes
    // Strip only line breaks / tabs (true garbage from bad data). Do NOT strip
    // ALL whitespace — S3 object keys legitimately contain spaces (e.g.
    // "Resume_Akashdeep Chik Baraik_...pdf"); removing them produced a
    // different, non-existent key so the file 404'd and wouldn't open.
    .replace(/[\r\n\t]+/g, '')
    .replace(/https:\/\/https:\/\//g, 'https://') // Fix double https://
    .replace(/https:\/\/https/g, 'https') // Fix https://https
    .replace(/https:\/\/http:\/\//g, 'https://') // Fix mixed protocols
    .replace(/https\/\//g, 'https://') // Fix missing colon: https// -> https://
    .replace(/http\/\//g, 'http://'); // Fix missing colon: http// -> http://

  // Percent-encode literal spaces that remain inside the URL so the browser
  // requests the exact S3 key (space -> %20). Skip anything already encoded.
  cleanedUrl = cleanedUrl.replace(/ /g, '%20');

  return cleanedUrl;
};

/**
 * Validates and normalizes a URL for opening in a new window
 * @param {string} url - The URL to validate and open
 * @returns {string|null} - The validated URL or null if invalid
 */
export const validateAndOpenUrl = (url) => {
  if (!url) {
    console.error("URL is not available");
    return null;
  }

  const cleanedUrl = cleanUrl(url);

  // Check if cleanedUrl is a valid absolute URL
  const isValidUrl = cleanedUrl.startsWith("http://") || cleanedUrl.startsWith("https://");
  
  let finalUrl = cleanedUrl;
  
  if (!isValidUrl) {
    // If it's a relative path, construct a full URL
    if (cleanedUrl.startsWith("//")) {
      // Protocol-relative URL (//example.com)
      finalUrl = `${window.location.protocol}${cleanedUrl}`;
    } else if (cleanedUrl.startsWith("/")) {
      // Absolute path (/path/to/page)
      finalUrl = `${window.location.origin}${cleanedUrl}`;
    } else {
      // Assume it's a domain without protocol
      finalUrl = `https://${cleanedUrl}`;
    }
  }
  
  // Validate the final URL
  try {
    new URL(finalUrl); // This will throw if URL is invalid
    return finalUrl;
  } catch (error) {
    console.error("Invalid URL:", finalUrl, error);
    return null;
  }
};

/**
 * Opens a cleaned and validated URL in a new window
 * @param {string} url - The URL to open
 * @param {string} target - The target window (default: "_blank")
 * @returns {boolean} - True if URL was opened successfully, false otherwise
 */
export const openCleanUrl = (url, target = "_blank") => {
  const finalUrl = validateAndOpenUrl(url);
  
  if (finalUrl) {
    window.open(finalUrl, target);
    return true;
  } else {
    console.error("Cannot open URL: Invalid or missing URL");
    return false;
  }
};

/**
 * Opens a resume for inline preview in a new tab.
 * - PDF: opens directly so the browser's native viewer renders it inline.
 *   (Note: if the S3 object was stored with `Content-Disposition: attachment`,
 *   the browser still downloads it — that must be fixed on the backend/upload side.)
 * - DOCX/DOC: routed through the Microsoft Office Online viewer so it renders
 *   inline instead of downloading. Requires a publicly reachable URL (our resume
 *   URLs are public S3 objects).
 * - Anything else: opens directly and lets the browser decide.
 * @param {string} url - resume file URL (public S3/CDN)
 * @returns {boolean} - true if a tab was opened, false on invalid/missing URL
 */
export const openResumeInNewTab = (url) => {
  const finalUrl = validateAndOpenUrl(url);
  if (!finalUrl) {
    console.error("Cannot open resume: invalid or missing URL");
    return false;
  }

  // Drop any query string (signed-URL params) before reading the extension.
  const pathOnly = finalUrl.split("?")[0].toLowerCase();
  const isOfficeDoc = pathOnly.endsWith(".docx") || pathOnly.endsWith(".doc");

  const target = isOfficeDoc
    ? `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(finalUrl)}`
    : finalUrl;

  window.open(target, "_blank", "noopener,noreferrer");
  return true;
};

/**
 * Navigates to a posted job's view page with the invite modal pre-opened.
 * Use this after any successful job-post action to keep navigation consistent.
 * @param {Function} navigate - react-router navigate function
 * @param {string|number} jobId - the posted job's id
 * @param {boolean} [replace=true] - whether to replace the history entry
 */
export const navigateToPostedJob = (navigate, jobId, replace = true, skipInviteModal = false) => {
  navigate(`/dashboard/my-jobs/myJobs/${jobId}/view-jobs-data`, {
    state: { showInviteModal: !skipInviteModal },
    replace,
  });
};

/**
 * Navigates to the Draft Jobs list page after saving a draft.
 * @param {Function} navigate - react-router navigate function
 * @param {boolean} [replace=true] - whether to replace the history entry
 */
export const navigateToDraftJobs = (navigate, replace = true) => {
  navigate(`/dashboard/my-jobs/draftJobs`, { replace });
};

/**
 * Detects the current environment based on URL hostname or environment variable
 * @returns {string} - The environment: "dev", "qat", "pre", or "prod"
 */
export const getEnvironment = () => {
  // First check the environment variable (set during build)
  if (import.meta.env.VITE_ENVIRONMENT) {
    return import.meta.env.VITE_ENVIRONMENT;
  }

  // Fallback: detect from URL hostname
  const hostname = window.location.hostname.toLowerCase();

  // localhost or local development
  if (hostname === "localhost" || hostname === "127.0.0.1" || hostname.includes("192.168.")) {
    return "dev";
  }

  // Check for environment indicators in hostname
  if (hostname.includes("dev.") || hostname.includes("-dev") || hostname.includes("dev-")) {
    return "dev";
  }

  if (hostname.includes("qat.") || hostname.includes("-qat") || hostname.includes("qat-")) {
    return "qat";
  }

  if (hostname.includes("pre.") || hostname.includes("-pre") || hostname.includes("pre-") || hostname.includes("staging")) {
    return "pre";
  }

  // Default to prod for production URLs or unrecognized hostnames
  return "prod";
};

/**
 * Read `mailtoken` from a raw query string (e.g. `location.search`).
 * Do not use `URLSearchParams.get("mailtoken")`: in `application/x-www-form-urlencoded`
 * rules, `+` is treated as a space, which breaks tokens that embed emails like `user+tag@...`.
 * @param {string} [search] - `location.search` or any string containing `?...&mailtoken=...`
 * @returns {string|null} decoded value or null if missing
 */
export function getMailtokenFromSearch(search) {
  if (search == null || typeof search !== "string") return null;
  const m = search.match(/[?&]mailtoken=([^&]*)/i);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return m[1];
  }
}
